import React, { useMemo, useRef, useState, useEffect } from 'react'
import DataGrid from 'react-data-grid'
import './index.css'
import { getTrainingCompleted } from '../api'
import {
    formatDate,
    formatDateAndTime,
} from '../../../resources/empresources/utils/index'
import { DepartmentRows, ProductRow } from './types'
import { useAppDispatch } from '../../../../redux/hook'
import { HIDE_LOADER, SHOW_LOADER } from '../../../../redux/slices/LoaderSlice'

const Trainingcompleted: React.FC<{
    otherUserId: string | null
    isActive: boolean
}> = ({ otherUserId, isActive }) => {
    const dispatch = useAppDispatch()
    const [rows, setRows] = useState<DepartmentRows[]>([])
    const productsMap = useRef<Map<number, ProductRow[]>>(new Map()).current

    useEffect(() => {
        const fetchData = async () => {
            dispatch(SHOW_LOADER())
            try {
                const param = {
                    userId: otherUserId,
                }
                const response = await getTrainingCompleted(param)
                const data = response.data
                const departments: DepartmentRows[] = data.map(
                    (item: any, index: number) => ({
                        id: index + 1,
                        type: 'MASTER',
                        program: item.programName,
                        assigned: item.assignedOn
                            ? formatDate(item.assignedOn)
                            : '',
                        completed: item.completedOn
                            ? formatDate(item.completedOn)
                            : '',
                        status: item.status,
                        expanded: false,
                    })
                )
                setRows(departments)

                data.forEach((item: any, index: number) => {
                    const products: ProductRow[] = item.programTaskDto.map(
                        (task: any, taskIndex: number) => ({
                            id: taskIndex + 1,
                            task: task.taskName,
                            category: task.category,
                            score:
                                task?.scoredMarks !== null
                                    ? `${task.scoredMarks}/${task.passingScore}`
                                    : '--',
                            viewed: task.viewedOn
                                ? formatDateAndTime(task.viewedOn)
                                : '',
                        })
                    )
                    productsMap.set(index + 1, products)
                })
            } catch (error) {
                console.error(error)
            } finally {
                dispatch(HIDE_LOADER())
            }
        }
        if (isActive) {
            fetchData()
        }
    }, [productsMap, otherUserId, isActive])

    function CellExpanderFormatter({ tabIndex, expanded, onCellExpand }: any) {
        function handleKeyDown(e: React.KeyboardEvent<HTMLSpanElement>) {
            if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault()
                onCellExpand()
            }
        }

        return (
            <div className={'icon'}>
                <span onClick={onCellExpand} onKeyDown={handleKeyDown}>
                    <span tabIndex={tabIndex}>
                        {expanded ? '\u25BC' : '\u25B6'}
                    </span>
                </span>
            </div>
        )
    }

    function ProductGrid({
        parentId,
        productsMap,
    }: {
        parentId: number
        productsMap: Map<number, ProductRow[]>
    }) {
        const gridRef = useRef<any>(null)
        const products = productsMap.get(parentId) || []

        function rowKeyGetter(row: ProductRow) {
            return row.id
        }

        const productColumns: any = [
            { key: 'task', name: 'Training Task', sortable: true },
            { key: 'category', name: 'Category', sortable: true },
            { key: 'score', name: 'Score', sortable: true },
            { key: 'viewed', name: 'Viewed On', sortable: true },
        ]

        return (
            <DataGrid
                ref={gridRef}
                rows={products}
                columns={productColumns}
                rowKeyGetter={rowKeyGetter}
                style={{ blockSize: 250 }}
                className="training-details"
            />
        )
    }

    const columns = useMemo((): any => {
        return [
            {
                key: 'expanded',
                name: '',
                minWidth: 30,
                width: 30,
                colSpan(args: any) {
                    return args.type === 'ROW' && args.row.type === 'DETAIL'
                        ? 5
                        : undefined
                },
                cellClass(row: any) {
                    return row.type === 'DETAIL'
                        ? `cell-with-padding`
                        : undefined
                },
                renderCell({ row, tabIndex, onRowChange }: any) {
                    if (row.type === 'DETAIL') {
                        return (
                            <ProductGrid
                                parentId={row.parentId!}
                                productsMap={productsMap}
                            />
                        )
                    }

                    return (
                        <CellExpanderFormatter
                            expanded={row.expanded}
                            tabIndex={tabIndex}
                            onCellExpand={() => {
                                onRowChange({ ...row, expanded: !row.expanded })
                            }}
                        />
                    )
                },
            },
            {
                key: 'program',
                name: 'Training Program',
                sortable: true,
            },
            { key: 'assigned', name: 'Assigned On', sortable: true },
            { key: 'completed', name: 'Completed On', sortable: true },
            {
                key: 'status',
                name: 'Status',
                sortable: true,
                renderCell: (props: any) => {
                    return (
                        <div
                            className={
                                props.row.status === 'Completed'
                                    ? 'comp'
                                    : 'status'
                            }
                        >
                            {props.row.status}
                        </div>
                    )
                },
            },
        ]
    }, [productsMap])

    function onRowsChange(rows: DepartmentRows[], { indexes }: any) {
        const row = rows[indexes[0]]
        if (row.type === 'MASTER') {
            if (row.expanded) {
                rows.splice(indexes[0] + 1, 0, {
                    type: 'DETAIL',
                    id: row.id + 100,
                    parentId: row.id,
                })
            } else {
                rows.splice(indexes[0] + 1, 1)
            }
            setRows([...rows])
        }
    }

    function rowKeyGetter(row: DepartmentRows | ProductRow) {
        return row.id
    }

    return (
        <div>
            <div className="table-wrapper grid-table-timeoff mt-4">
                {rows.length === 0 ? (
                    <div className="no-records">No records found</div>
                ) : (
                    <DataGrid
                        rowKeyGetter={rowKeyGetter}
                        columns={columns}
                        rows={rows}
                        onRowsChange={onRowsChange}
                        headerRowHeight={45}
                        rowHeight={(row) => (row.type === 'DETAIL' ? 300 : 45)}
                        className="data-grid-table"
                        enableVirtualization={false}
                        onCellKeyDown={(_, event) => {
                            if (event.isDefaultPrevented()) {
                                // skip parent grid keyboard navigation if nested grid handled it
                                event.preventGridDefault()
                            }
                        }}
                    />
                )}
            </div>
        </div>
    )
}

export default Trainingcompleted
